import { computed, observable, reaction, runInAction } from "mobx"

import { withGlobalLoader } from "@/lib/context.ts"
import { Model } from "@/lib/Model.ts"
import { optimisticAction } from "@/lib/std.ts"
import { Navigator } from "@/routes/Navigator.ts"
import { TaskViewId } from "@/routes/tasks/TaskViewId.ts"
import { allScriptsStarted } from "@/routes/tasks/TaskViewScene/TaskPreviewModel.ts"
import { TabId } from "@/routes/tasks/TaskViewScene/TaskTabId.ts"

import type { RouterType } from "@/context.ts"
import type {
  InitRuntimeConfigTaskState,
  ProcessRunState,
  RuntimeConfig,
  TID
} from "@/lib/client/generated/TypeDefinitions.ts"
import type { OrgContext } from "@/lib/context.ts"
import type { Queries } from "@/lib/queries.ts"
import type { TaskViewModel } from "@/routes/tasks/TaskViewScene/TaskViewModel.ts"

export class TaskRuntimeConfigFileModel extends Model {
  private readonly router: RouterType
  private readonly context: OrgContext
  private readonly queries: Queries
  readonly taskViewModel: TaskViewModel

  readonly runtimeState = observable.box<InitRuntimeConfigTaskState | null | undefined>(null)

  readonly rootFolder = observable.box("")
  readonly beforeCommand = observable.box("")
  readonly previewCommands = observable.box<RuntimeConfig.RunAppCommand[]>([])

  readonly runtimeEnvDescription = observable.box<string | null>(null)

  readonly isEnvActive = computed(() => {
    return this.taskViewModel.previewProcessesState?.get()?.className == "Running"
  })

  readonly isAnyScriptRunning = computed(() => {
    const processState = this.taskViewModel.previewProcessesState?.get()
    if (processState == null || processState.className != "Running") {
      return false
    }

    if (processState.warmupScriptState.className == "Running") {
      return true
    }

    return processState.processStates
      .map(({ second: processRunState }) => processRunState)
      .some(processRunState => processRunState.className === "Running" && !processRunState.ready)
  })

  readonly previewProcessesState = computed(() => this.taskViewModel.previewProcessesState.get())

  readonly previewCommandsState = computed(() => {
    const commands = this.previewCommands.get()
    const processState = this.previewProcessesState.get()
    if (processState?.className !== "Running") {
      return commands.map((cmd) => ({
        command: cmd,
        state: { className: "NotStarted" } satisfies ProcessRunState.NotStarted
      }))
    }

    return commands.map((cmd) => {
      const state = processState.processStates.find((p) => p.first.id == cmd.id)?.second
      return {
        command: cmd,
        state: state || { className: "NotStarted" } satisfies ProcessRunState.NotStarted
      }
    })
  })

  constructor(params: {
    readonly context: OrgContext
    readonly queries: Queries,
    readonly router: RouterType,
    readonly taskViewModel: TaskViewModel,
  }) {
    super("TaskRuntimeConfigFileModel")

    this.router = params.router
    this.context = params.context
    this.queries = params.queries
    this.taskViewModel = params.taskViewModel

    this.lifetime.add(reaction(
      () => this.taskViewModel.previewProcessesState.get(),
      (current, prev) => {
        if (allScriptsStarted(current) && !allScriptsStarted(prev)) {
          this.taskViewModel.invalidateTaskQuery(this.taskViewModel.taskId)
          void this.router.navigate(Navigator.task(this.context.orgId, this.taskViewModel.project.get().key, this.taskViewModel.taskId, {
            view: TaskViewId.Full,
            tab: TabId.Preview
          }))
        }
      }), "TaskRuntimeConfigFileModel::switchToPreviewTab")

    const runtimeConfig = this.taskViewModel.runtimeConfig.get()

    runInAction(() => {
      this.runtimeState.set(this.taskViewModel.task.get()?.initRuntimeConfigState)

      this.rootFolder.set(runtimeConfig?.rootFolder || "")
      this.beforeCommand.set(runtimeConfig?.beforeCommand || "")
      this.updatePreviewCommands(runtimeConfig)
    })

    this.lifetime.add(reaction(
      () => (
        {
          runtimeConfig: this.taskViewModel.runtimeConfig.get()
        }
      ),
      ({ runtimeConfig }) => {
        this.rootFolder.set(runtimeConfig?.rootFolder || "")
        this.beforeCommand.set(runtimeConfig?.beforeCommand || "")
        this.updatePreviewCommands(runtimeConfig)
      }
    ), "TaskRuntimeConfigFileModel:runtimeConfig")

    void this.queries.getRuntimeEnvironmentDescription(this.taskViewModel.taskId as TID)
      .then(text => runInAction(() => this.runtimeEnvDescription.set(text)))
  }

  private updatePreviewCommands(runtimeConfig: RuntimeConfig | null) {
    if (this.taskViewModel.isMultiPreview.get() == true) {
      this.previewCommands.set(runtimeConfig?.previews?.map((service) => ({
          id: service.id,
          title: service.title,
          description: service.description,
          command: service.previewScript
        }
      )) || [])
    } else {
      this.previewCommands.set(runtimeConfig?.previewCommands || [])
    }
  }

  readonly handleBeforeCommandChange = async (value: string) => {
    const current = this.beforeCommand.get()
    await optimisticAction(
      () => this.beforeCommand.set(value),
      () => this.queries.apiClient.saveBeforeCommand(this.taskViewModel.taskId, value),
      () => this.beforeCommand.set(current)
    )
  }

  readonly handlePreviewRunCommandChange = async (index: number, value: string) => {
    const command = this.previewCommands.get().at(index)
    if (!command) return

    const previousCommands = this.previewCommands.get().slice()
    const nextCommands = previousCommands.slice()
    if (index >= 0 && index < nextCommands.length) {
      nextCommands[index] = { ...nextCommands[index], command: value }
    }

    await optimisticAction(
      () => this.previewCommands.set(nextCommands),
      () => this.queries.apiClient.savePreviewCommand(this.taskViewModel.taskId, command.id, value),
      () => this.previewCommands.set(previousCommands)
    )
  }

  readonly handleRecreatePreviewRuntime = async () => {
    await withGlobalLoader(this.context, this.queries.apiClient.recreatePreview(this.taskViewModel.taskId))
  }

  readonly handleRunBeforeCommand = async () => {
    await withGlobalLoader(this.context, this.queries.apiClient.verifyBeforeCommand(this.taskViewModel.taskId))
  }

  readonly handleRunPreviewCommand = async (index: number) => {
    const cmd = this.previewCommands.get()[index]
    if (!cmd) return
    await withGlobalLoader(this.context, this.queries.apiClient.verifyPreviewCommand(this.taskViewModel.taskId, cmd.id))
  }

  readonly handleStopBeforeCommand = async () => {
    await withGlobalLoader(this.context, this.queries.apiClient.stopBeforeCommand(this.taskViewModel.taskId))
  }

  readonly handleStopPreviewCommand = async (commandId: string) => {
    await withGlobalLoader(this.context, this.queries.apiClient.stopPreviewCommand(this.taskViewModel.taskId, commandId))
  }

  readonly handleAddPreviewCommand = async (id: string, title: string, script: string, port: number | null) => {
    await withGlobalLoader(this.context, this.queries.apiClient.addPreviewCommand(this.taskViewModel.taskId, id, title, script, port))
  }

  readonly handleRemovePreviewCommand = async (commandId: string) => {
    await withGlobalLoader(this.context, this.queries.apiClient.removePreviewCommand(this.taskViewModel.taskId, commandId))
  }

  readonly handleReorderPreviewCommands = async (oldIndex: number, newIndex: number) => {
    const currentCommands = this.previewCommands.get()
    const reorderedCommands = [...currentCommands]
    const [movedCommand] = reorderedCommands.splice(oldIndex, 1)
    reorderedCommands.splice(newIndex, 0, movedCommand)

    const commandIds = reorderedCommands.map(cmd => cmd.id)

    await optimisticAction(
      () => this.previewCommands.set(reorderedCommands),
      () => this.queries.apiClient.reorderPreviewCommands(this.taskViewModel.taskId, commandIds),
      () => this.previewCommands.set(currentCommands)
    )
  }
}
